if (!customElements.get('form-wrapper')) {
  class FormWrapper extends HTMLElement {
    constructor() {
      super();
      this.styleSheets = this.querySelectorAll('link[rel="stylesheet"]');
      this.injectedDOM = null;
      this.embedEl = null;
      this.successWatching = false;
      this.isModalForm = this.classList.contains('form-wrapper--modal');
      this.prefillData = this.dataset.prefill ? JSON.parse(this.dataset.prefill) : null;
      this.prefillCheckbox = this.dataset.prefillCheckbox === 'true';
      this.externalButton = this.querySelector('.form-wrapper__footer button[data-action="submit-form"]');
      this.expiredReferralCodes = this.dataset.expiredReferralCodes
        ? JSON.parse(this.dataset.expiredReferralCodes)
        : [];
      this.referrerCodeErrorTemplate = this.querySelector('template[data-referrer-code-error]');
      this.hidePhoneNumber = this.dataset.hidePhoneNumber === 'true';
    }

    connectedCallback() {
      this.attackObserver();

      this.addEventListener('shopify-form-submitted', (e) => {
        this.classList.add('form-submitted-successfully');
        const afterSubmitEls = this.querySelectorAll('.form-wrapper__after-submit');
        this.externalButton?.classList.add('hidden');
        if (afterSubmitEls.length) {
          this.embedEl.classList.remove('shadow-root-updated');
          afterSubmitEls.forEach((el) => el.classList.remove('hidden'));
        }
        if (this.isModalForm) {
          const modal = this.closest('.hide-button-on-submit');
          if (modal) {
            const triggerButton = document.querySelector(`[aria-controls="${modal.id}"]`);
            if (triggerButton) {
              triggerButton.classList.add('hidden');
            }
          }
        }
      });
    }

    attackObserver() {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

          for (const node of mutation.addedNodes) {
            if (node.nodeType !== Node.ELEMENT_NODE) continue;
            const el = /** @type {Element} */ (node);

            if (!this.embedEl) {
              if (el.localName === 'shopify-forms-embed') {
                this.embedEl = el;
              } else {
                this.embedEl = el.querySelector('shopify-forms-embed');
              }
            }
          }
        }

        // Detect shadow root creation after mutations and populate class var.
        this.modifyShadowRoot();
        this.validateExpiredReferralCodes();
      });

      observer.observe(this, { childList: true, subtree: true });
    }

    validateExpiredReferralCodes() {
      if (!this.embedEl || !this.embedEl.shadowRoot) return;
      const referrerCodeInput = this.embedEl.shadowRoot.getElementById('custom#referrer_code');
      if (!referrerCodeInput) return;
      const inputWrapper = referrerCodeInput?.parentNode;
      if (!this.referrerCodeErrorTemplate) return;
      referrerCodeInput.addEventListener('blur', () => {
        const value = referrerCodeInput.value;
        if (this.expiredReferralCodes.includes(value)) {
          const errorOut = document.importNode(this.referrerCodeErrorTemplate.content, true);
          inputWrapper?.parentNode.insertBefore(errorOut, inputWrapper.nextSibling);
        } else {
          inputWrapper?.parentNode.querySelector(`.form-wrapper__error[for="${referrerCodeInput.id}"]`)?.remove();
        }
      });
    }

    modifyShadowRoot() {
      if (!this.embedEl) return;
      const shadowEl = this.embedEl.shadowRoot;
      if (!shadowEl) return;
      this.styleSheets.forEach((link) => {
        const clonedLink = link.cloneNode();
        shadowEl.appendChild(clonedLink);
      });
      if (this.hidePhoneNumber) {
        this.phoneNumberHandling('phone_number', '', shadowEl);
      }
      if (this.prefillData) {
        this.prefillForm(shadowEl);
      }
      if (this.prefillCheckbox) {
        this.prefillCheckboxes(shadowEl);
      }
      this.embedEl.classList.add('shadow-root-updated');
      if (this.isModalForm) {
        shadowEl.querySelector('#app-embed')?.classList.add('form-wrapper__modal');
        this.externalButton?.addEventListener('click', () => {
          this.externalButton.setAttribute('aria-busy', 'true');
          this.externalButton.setAttribute('disabled', 'true');
          shadowEl.querySelector("form button[type='submit']")?.click();
        });
      }
      shadowEl.querySelector('form')?.addEventListener('submit', this.observeSuccess.bind(this));
    }

    emitInput(el, eventType) {
      el.dispatchEvent(new Event(eventType, { bubbles: true }));
    }

    prefillCheckboxes(shadowEl) {
      const checkboxes = shadowEl.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((checkbox) => {
        checkbox.checked = true;
        this.emitInput(checkbox, 'change');
      });
    }

    phoneNumberHandling(key, rawValue, shadowEl) {
      if (key !== 'phone_number') return;
      const inputById = shadowEl.getElementById(key);
      if (!inputById) return;
      if (rawValue || this.hidePhoneNumber) {
        const wrapper = inputById.closest('form > div');
        if (wrapper) {
          wrapper.remove();
        }
      }
    }

    prefillForm(shadowEl) {
      for (const [key, rawValue] of Object.entries(this.prefillData || {})) {
        if (!rawValue) continue;
        const value = String(rawValue ?? '');
        if (key === 'phone_number') {
          this.phoneNumberHandling(key, value, shadowEl);
          continue;
        }

        if (key === 'custom#birth_date') {
          const [year = '', month = '', day = ''] = value.split('-');
          const byLabel = {
            'label-MM': month,
            'label-DD': day,
            'label-YYYY': year,
          };

          shadowEl.querySelectorAll('._dateFieldContainer_rzr7e_5 input').forEach((el) => {
            const next = byLabel[el.getAttribute('aria-labelledby')];
            if (next !== undefined) {
              el.value = next;
              this.emitInput(el, 'input');
            }
          });
          continue;
        }

        const inputById = shadowEl.getElementById(key);
        if (key === 'custom#phone_prefix' && rawValue) {
          const prefixInput = shadowEl.querySelector('[aria-controls*="custom#phone_prefix"]');
          prefixInput?.parentNode?.nextSibling?.remove();
          prefixInput?.parentNode?.remove();
        }

        if (inputById) {
          if (key === 'custom#referrer_code' && rawValue) {
            inputById?.parentNode?.remove();
            continue;
          }
          inputById.value = inputById.type === 'number' ? Number(value) : value;
          this.emitInput(inputById, 'input');
          if (key === 'email') {
            inputById.closest('._formFieldContainer_1mxsl_5 ')?.classList.add('hidden');
          }
          continue;
        }

        const optionInput = shadowEl.getElementById(`${key}-${value}`);
        if (optionInput) {
          optionInput.checked = true;
          this.emitInput(optionInput, 'change');
        }
      }
    }

    observeSuccess() {
      if (this.successWatching) return;
      this.successWatching = true;
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type !== 'attributes' || (mutation.attributeName !== 'data-current-step' && mutation.attributeName !== 'aria-invalid')) continue;
          if (mutation.attributeName === 'aria-invalid') {
            this.externalButton.removeAttribute('aria-busy');
            this.externalButton.removeAttribute('disabled');
            continue;
          }
          // dispatch event form submitted on custom element
          this.dispatchEvent(
            new CustomEvent('shopify-form-submitted', {
              detail: {
                formWrapper: this,
              },
            }),
          );
          observer.disconnect();
          break;
        }
      });
      observer.observe(this.embedEl.shadowRoot, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }
  }
  customElements.define('form-wrapper', FormWrapper);
}
